import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import { PillButton } from 'js/design_system/PillButton';
import { AnalyticsPeriod } from 'js/analytics_v2/types';
import {
  AnalyticsSessionContext,
  useAnalyticsHref,
} from 'js/analytics_v2/navigation';
import {
  analyticsTabs,
  AnalyticsTab,
  analyticsLandingPath,
  analyticsSourcePage,
} from 'js/analytics_v2/tabs';

const Navigation = styled.nav`
  background: var(--background-white, #ffffff);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

const Toolbar = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacing-sm-24, 24px);
  margin: 0 auto;
  max-width: 1170px;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-16, 16px);
`;

const NavigationLinks = styled.div`
  display: flex;
  flex: 1;
  gap: var(--spacing-xs-12, 12px);
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;

  @import 'stylesheets/design_system/breakpoint';
  @include breakpoint(small) {
    /* Hide the scrollbar but keep swipe scrolling, like MentionsSubNav. */
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const NavigationLink = styled(TextButtonLink)`
  border-bottom: 3px solid transparent;
  color: var(--text-primary, #141413);
  flex: 0 0 auto;
  height: 44px;
  padding: 0 var(--spacing-xs-4, 4px);
  text-decoration: none;

  &[aria-current='page'] {
    border-bottom-color: var(--primary-brand, #0645b1);
    color: var(--text-link, #0645b1);
    font-weight: 600;
  }
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: var(--spacing-xs-12, 12px);

  @import 'stylesheets/design_system/breakpoint';
  @include breakpoint(small) {
    display: none;
  }
`;

const PeriodControls = styled.div`
  display: flex;
`;

const PeriodButton = styled(PillButton)`
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-dark, #141413);
  border-radius: 0;
  color: var(--text-primary, #141413);
  font-size: var(--type-body-md-size, 16px);
  height: 36px;
  line-height: var(--type-body-md-line-height, 20px);
  outline: none;
  padding: 0 var(--spacing-xs-12, 12px);

  &:first-child {
    border-radius: var(--border-radius-radius-md, 8px) 0 0
      var(--border-radius-radius-md, 8px);
  }

  & + & {
    margin-left: -1px;
  }

  &:last-child {
    border-radius: 0 var(--border-radius-radius-md, 8px)
      var(--border-radius-radius-md, 8px) 0;
  }

  &[aria-pressed='true'] {
    background: var(--background-light-blue, #eef2f9);
    border-color: var(--text-press, #082f75);
    color: var(--text-press, #082f75);
    outline: none;
    position: relative;
    z-index: 1;
  }
`;

const DownloadButton = styled(Button)`
  height: 36px;
  min-width: 36px;
  padding: 0;
  width: 36px;

  &:disabled {
    cursor: not-allowed;
  }
`;

type Props = {
  activeItem: (typeof analyticsTabs)[number]['label'];
  analyticsBasePath: string;
  period?: AnalyticsPeriod;
  onPeriodChange?: (period: AnalyticsPeriod) => void;
  controls?: React.ReactNode;
  nonPremium?: boolean;
  onTabClick?: (tab: AnalyticsTab) => void;
};

export const AnalyticsV2NavBar: React.FC<Props> = ({
  activeItem,
  analyticsBasePath,
  period,
  onPeriodChange,
  controls,
  nonPremium,
  onTabClick,
}) => {
  const href = useAnalyticsHref();
  const session = useContext(AnalyticsSessionContext);
  const needsUpgrade =
    nonPremium ?? session?.context.access_mode === 'non_premium';
  const analyticsPath = (suffix: string) =>
    href(`${analyticsBasePath.replace(/\/$/, '')}${suffix}`);

  return (
    <Navigation aria-label="Analytics sections">
      <Toolbar>
        <NavigationLinks>
          {analyticsTabs.map(({ label, key, path }) => (
            <NavigationLink
              aria-current={label === activeItem ? 'page' : undefined}
              href={
                needsUpgrade &&
                (
                  ['readers', 'profile_visitors', 'cv'] as AnalyticsTab[]
                ).includes(key)
                  ? href(
                      analyticsLandingPath(
                        analyticsSourcePage(window.location.pathname),
                        `analytics-tab-${key}`,
                      ),
                    )
                  : analyticsPath(`/activity/${path}`)
              }
              inline
              key={key}
              onClick={() => onTabClick?.(key)}
              size="sm"
            >
              {label}
            </NavigationLink>
          ))}
        </NavigationLinks>
        <Controls>
          {controls}
          {period && onPeriodChange && (
            <PeriodControls role="group" aria-label="Analytics period">
              <PeriodButton
                selected={period === '30d'}
                aria-pressed={period === '30d'}
                onClick={() => onPeriodChange('30d')}
                data-analytics-action="select_period"
                data-analytics-value="30d"
              >
                30 days
              </PeriodButton>
              <PeriodButton
                selected={period === '60d'}
                aria-pressed={period === '60d'}
                onClick={() => onPeriodChange('60d')}
                data-analytics-action="select_period"
                data-analytics-value="60d"
              >
                60 days
              </PeriodButton>
            </PeriodControls>
          )}
          {!controls && (
            <DownloadButton
              size="xs"
              icon="download"
              aria-label="Download analytics"
              disabled
            />
          )}
        </Controls>
      </Toolbar>
    </Navigation>
  );
};
